/**
 * Debounce a fast-changing value — in practice, a search box.
 *
 * List pages drive their fetch off a `useEffect` keyed on the filter state, so
 * binding a text input straight to that state fires one request per keystroke.
 * Keep the raw value in state (so the input stays responsive and controlled) and
 * feed the debounced value to the effect instead:
 *
 * ```tsx
 * const [search, setSearch] = useState('')
 * const debouncedSearch = useDebouncedValue(search)
 * useEffect(() => { load() }, [page, debouncedSearch])
 * ```
 *
 * @param value   The value to debounce.
 * @param delayMs How long the value must hold still before it is published.
 */
import { useEffect, useState } from 'react'

export function useDebouncedValue<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState<T>(value)

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs)
    return () => window.clearTimeout(timer)
  }, [value, delayMs])

  return debounced
}
